/**
 * Email Branding Settings
 * Allows admin to customize email template colors (header, footer, background, text, etc.)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { 
  Mail, Save, RefreshCw, Loader2, Palette, Eye, 
  RotateCcw, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Default colors matching the original email template
const DEFAULT_BRANDING = {
  logo_url: "https://customer-assets-jt897jd0.emergentagent.net/job_daily-notes-84/artifacts/7qbqgk18_UnCloned%C2%AE%20Journal-Color%20Logo.png",
  header_background: "#1a0527",
  header_gradient_end: "#2D063E",
  accent_bar_start: "#df1283",
  accent_bar_end: "#FF7901",
  body_background: "#f5f5f5",
  content_background: "#ffffff",
  text_color: "#333333",
  footer_background: "#fafafa",
  footer_text_color: "#666666",
  link_color: "#df1283",
  button_color: "#df1283",
  button_text_color: "#ffffff",
};

// Color picker with label
const ColorPicker = ({ label, description, value, onChange, testId }) => (
  <div className="flex items-center gap-3 p-3 bg-[#1a0527]/50 rounded-lg border border-[#4A0A66]/30">
    <div className="flex-shrink-0">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded cursor-pointer border-2 border-[#4A0A66]"
        data-testid={testId}
      />
    </div>
    <div className="flex-1 min-w-0">
      <label className="text-white text-sm font-medium block">{label}</label>
      {description && (
        <p className="text-[#FDF2F8]/50 text-xs mt-0.5">{description}</p>
      )}
    </div>
    <div className="flex items-center gap-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-24 bg-[#4A0A66]/30 border-[#4A0A66] text-white text-xs font-mono"
        placeholder="#000000"
      />
    </div>
  </div>
);

const EmailBrandingSettings = () => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalBranding, setOriginalBranding] = useState(DEFAULT_BRANDING);

  const fetchBranding = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/settings/email-branding`, {
        withCredentials: true
      });
      const settings = res.data.settings || DEFAULT_BRANDING;
      setBranding(settings);
      setOriginalBranding(settings);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to fetch email branding:', err);
      toast.error('Failed to load email branding settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const handleColorChange = (key, value) => {
    setBranding(prev => {
      const updated = { ...prev, [key]: value };
      setHasChanges(JSON.stringify(updated) !== JSON.stringify(originalBranding));
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API_BASE}/api/admin/settings/email-branding`,
        { settings: branding },
        { withCredentials: true }
      );
      toast.success('Email branding saved successfully!');
      setOriginalBranding(branding);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save email branding:', err);
      toast.error('Failed to save email branding');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setBranding(DEFAULT_BRANDING);
    setHasChanges(JSON.stringify(DEFAULT_BRANDING) !== JSON.stringify(originalBranding));
  };

  const handleRevert = () => {
    setBranding(originalBranding);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#df1283]" />
            Email Branding Colors
          </h3>
          <p className="text-sm text-[#FDF2F8]/50 mt-1">
            Customize the colors used in all system emails
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleReset}
            variant="outline"
            size="sm"
            className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset to Default
          </Button>
          <Button
            data-testid="email-branding-refresh-button"
            onClick={fetchBranding}
            variant="outline"
            size="sm"
            className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Color Settings */}
        <div className="space-y-4">
          {/* Logo Section */}
          <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#4A0A66]/30">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <img src={branding.logo_url || DEFAULT_BRANDING.logo_url} alt="Logo" className="w-5 h-5 object-contain" />
              Email Logo
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-[#1a0527]/50 rounded-lg border border-[#4A0A66]/30">
                <div className="flex-1 min-w-0">
                  <label className="text-white text-sm font-medium block">Logo URL</label>
                  <p className="text-[#FDF2F8]/50 text-xs mt-0.5">Image displayed in email header (recommended: 200x50px)</p>
                </div>
              </div>
              <Input
                value={branding.logo_url || ''}
                onChange={(e) => handleColorChange('logo_url', e.target.value)}
                placeholder="https://your-domain.com/logo.png"
                className="bg-[#4A0A66]/30 border-[#4A0A66] text-white text-sm"
                data-testid="email-logo-url-input"
              />
              {branding.logo_url && (
                <div className="p-3 bg-[#1a0527] rounded-lg border border-[#4A0A66]/30 flex justify-center">
                  <img 
                    src={branding.logo_url} 
                    alt="Logo Preview" 
                    className="max-h-16 max-w-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Header Section */}
          <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#4A0A66]/30">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#FF7901]" />
              Email Header
            </h4>
            <div className="space-y-3">
              <ColorPicker
                label="Header Background"
                description="Gradient start color (top-left)"
                value={branding.header_background}
                onChange={(v) => handleColorChange('header_background', v)}
                testId="color-header-bg"
              />
              <ColorPicker
                label="Header Gradient End"
                description="Gradient end color (bottom-right)"
                value={branding.header_gradient_end}
                onChange={(v) => handleColorChange('header_gradient_end', v)}
                testId="color-header-gradient"
              />
            </div>
          </div>

          {/* Accent Bar */}
          <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#4A0A66]/30">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <div className="w-4 h-1 rounded bg-gradient-to-r from-[#df1283] to-[#FF7901]" />
              Accent Bar
            </h4>
            <div className="space-y-3">
              <ColorPicker
                label="Accent Start Color"
                description="Left side of the accent bar"
                value={branding.accent_bar_start}
                onChange={(v) => handleColorChange('accent_bar_start', v)}
                testId="color-accent-start"
              />
              <ColorPicker
                label="Accent End Color"
                description="Right side of the accent bar"
                value={branding.accent_bar_end}
                onChange={(v) => handleColorChange('accent_bar_end', v)}
                testId="color-accent-end"
              />
            </div>
          </div>

          {/* Body Colors */}
          <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#4A0A66]/30">
            <h4 className="text-white font-medium mb-3">Email Body</h4>
            <div className="space-y-3">
              <ColorPicker
                label="Outer Background"
                description="Color behind the email card"
                value={branding.body_background}
                onChange={(v) => handleColorChange('body_background', v)}
                testId="color-body-bg"
              />
              <ColorPicker
                label="Content Background"
                description="Email card background"
                value={branding.content_background}
                onChange={(v) => handleColorChange('content_background', v)}
                testId="color-content-bg"
              />
              <ColorPicker
                label="Text Color"
                description="Main email text color"
                value={branding.text_color}
                onChange={(v) => handleColorChange('text_color', v)}
                testId="color-text"
              />
              <ColorPicker
                label="Link Color"
                description="Color for hyperlinks"
                value={branding.link_color}
                onChange={(v) => handleColorChange('link_color', v)}
                testId="color-link"
              />
            </div>
          </div>

          {/* Footer Colors */}
          <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#4A0A66]/30">
            <h4 className="text-white font-medium mb-3">Footer</h4>
            <div className="space-y-3">
              <ColorPicker
                label="Footer Background"
                description="Footer section background"
                value={branding.footer_background}
                onChange={(v) => handleColorChange('footer_background', v)}
                testId="color-footer-bg"
              />
              <ColorPicker
                label="Footer Text Color"
                description="Footer text and copyright"
                value={branding.footer_text_color}
                onChange={(v) => handleColorChange('footer_text_color', v)}
                testId="color-footer-text"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#4A0A66]/30">
            <h4 className="text-white font-medium mb-3">Buttons</h4>
            <div className="space-y-3">
              <ColorPicker
                label="Button Color"
                description="CTA button background"
                value={branding.button_color}
                onChange={(v) => handleColorChange('button_color', v)}
                testId="color-button"
              />
              <ColorPicker
                label="Button Text Color"
                description="CTA button text"
                value={branding.button_text_color}
                onChange={(v) => handleColorChange('button_text_color', v)}
                testId="color-button-text"
              />
            </div>
          </div>
        </div>

        {/* Right Column - Live Preview */}
        <div className="space-y-4">
          <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#4A0A66]/30 sticky top-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#0FFFE2]" />
                Live Preview
              </h4>
            </div>
            
            {/* Email Preview */}
            <div 
              data-testid="email-branding-live-preview"
              className="rounded-lg overflow-hidden shadow-lg"
              style={{ backgroundColor: branding.body_background }}
            >
              <div className="p-4">
                <div 
                  className="rounded-lg overflow-hidden shadow-md"
                  style={{ backgroundColor: branding.content_background }}
                >
                  {/* Header */}
                  <div 
                    className="p-4 text-center"
                    style={{ 
                      background: `linear-gradient(135deg, ${branding.header_background} 0%, ${branding.header_gradient_end} 100%)`
                    }}
                  >
                    {branding.logo_url ? (
                      <img 
                        src={branding.logo_url} 
                        alt="Logo" 
                        className="max-h-10 max-w-[160px] mx-auto object-contain"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <div className={branding.logo_url ? 'hidden' : ''}>
                      <div className="text-white font-bold text-sm">UNCLONED</div>
                      <div className="text-white/70 text-xs">JOURNAL</div>
                    </div>
                  </div>
                  
                  {/* Accent Bar */}
                  <div 
                    className="h-1"
                    style={{ 
                      background: `linear-gradient(90deg, ${branding.accent_bar_start}, ${branding.accent_bar_end})`
                    }}
                  />
                  
                  {/* Content */}
                  <div className="p-4" style={{ color: branding.text_color }}>
                    <p className="font-semibold mb-2" style={{ color: branding.link_color }}>
                      Welcome to UnCloned!
                    </p>
                    <p className="text-sm mb-3">
                      Hi there, your account has been created successfully.
                    </p>
                    <p className="text-sm mb-4">
                      Learn more at{' '}
                      <span style={{ color: branding.link_color, textDecoration: 'underline' }}>
                        unclonedjournal.com
                      </span>
                    </p>
                    
                    {/* Button Preview */}
                    <div className="text-center">
                      <button
                        className="px-6 py-2 rounded-full text-sm font-semibold"
                        style={{ 
                          background: `linear-gradient(90deg, ${branding.button_color}, ${branding.accent_bar_end})`,
                          color: branding.button_text_color
                        }}
                      >
                        Sign In Now
                      </button>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div 
                    className="p-3 text-center border-t"
                    style={{ 
                      backgroundColor: branding.footer_background,
                      borderColor: '#eeeeee'
                    }}
                  >
                    <p className="text-xs" style={{ color: branding.footer_text_color }}>
                      Sent with love from{' '}
                      <span style={{ color: branding.link_color, fontWeight: 600 }}>
                        UnCloned Journal
                      </span>
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#999999' }}>
                      © 2026 UnCloned Journal
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-[#FDF2F8]/40 text-center mt-3">
              This preview shows how your branding will appear in system emails
            </p>
          </div>
        </div>
      </div>

      {/* Save Bar */}
      {hasChanges && (
        <div className="sticky bottom-4 bg-[#2D063E] border border-[#4A0A66] rounded-xl p-4 shadow-xl flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#FFD700]">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">You have unsaved changes</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="email-branding-discard-button"
              onClick={handleRevert}
              variant="outline"
              className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
            >
              Discard Changes
            </Button>
            <Button
              data-testid="email-branding-save-button"
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailBrandingSettings;
